import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { collection, getDocs, query, CollectionReference, where, getDoc, Timestamp, Firestore, addDoc, deleteDoc, doc, DocumentReference, serverTimestamp, setDoc, updateDoc } from '@firebase/firestore/lite';
import { useFirebase } from 'modules/shared/services/firebase.service';
import { getDocsIn } from 'modules/shared/helpers/firestore.getDocsIn.fn';
import { AddJobDto, Job, JobDto, Match, UpdateJobDto } from './job';
import { useGigup } from 'modules/shared/services/gigup.service';
import { Industry, Jobtype } from 'modules/shared/services/gigup';
import { Candidate, WeekRanges } from 'modules/candidate/services/candidate';
import { getAllMatches } from './getAllMatches.fn';
import { Employer } from 'modules/employers/services/employer';


type JobsContextType = {
  getJobs: () => Promise<Job[]>;
  getJobById: (id: string) => Promise<Job | undefined>;
  getAllCandidates: () => Promise<Candidate[]>;
  getMatchedCandidates: (jobId: string) => Promise<Candidate[]>;
  saveMatchedCandidates: (jobId: string, oldCandidates: Candidate[], newCandidates: Candidate[]) => Promise<void>;
  getEmployerJobs: (id: string) => Promise<Job[]>;
  addJob: (job: AddJobDto, employer: Employer) => Promise<void>;
  updateJob: (id: string, job: UpdateJobDto) => Promise<void>;
  deleteJob: (job: Job) => Promise<void>;
};


const JobsContext = createContext<JobsContextType>(null!);


function prepareJob(job: JobDto, ref: DocumentReference<JobDto>, allIndustries: Industry[], allJobtypes: Jobtype[]) {
  const {status, ...data } = job; // exclude status (TODO: add status handling)
  return { ...data,
    ref,
    firstDay: job.firstDay.toDate(),
    lastDay: job.lastDay?.toDate(),
    dateCreated: job.dateCreated.toDate(),
    industries: allIndustries.filter(i => job.industries.find(ref => ref.id === i.uid)),
    jobtypes: allJobtypes.filter(j => job.jobtypes.find(ref => ref.id === j.uid)),
    weekRanges: job.weekRanges?.map(day =>
      ({ dayRanges: day.dayRanges.map(range => ({
        start: range.start && range.start.toDate(),
        end: range.end && range.end.toDate()
      }))})
    )
  } as Job;
}

async function getJobById(db: Firestore, id: string, allIndustries: Industry[], allJobtypes: Jobtype[]): Promise<Job | undefined> {
  const jobRef = doc(collection(db, 'guJobs') as CollectionReference<JobDto>, id);
  const job = (await getDoc(jobRef)).data();
  return job && prepareJob(job, jobRef, allIndustries, allJobtypes);
}


async function getJobs(db: Firestore, allIndustries: Industry[], allJobtypes: Jobtype[]): Promise<Job[]> {
  const q = query(
    collection(db, 'guJobs') as CollectionReference<JobDto>,
  );

  return (await getDocs(q)).docs.map(d => prepareJob(d.data(), d.ref, allIndustries, allJobtypes));
}


async function getEmployerJobs(db: Firestore, employerUid: string, allIndustries: Industry[], allJobtypes: Jobtype[]): Promise<Job[]> {
  const employer = doc(db, 'guEmployers/' + employerUid);
  const q = query(
    collection(db, 'guJobs') as CollectionReference<JobDto>,
    where('employer', '==', employer)
  );
  return (await getDocs(q)).docs.map(d => prepareJob(d.data(), d.ref, allIndustries, allJobtypes));

  // console.log(user.uid);

  // let jobList: any[] = [];

  // await Promise.all((await getDocs(q)).docs.map(async (doc) => {
  //   const refStatus = await getDoc(doc.data().status);
  //   const status = refStatus.data()?.name;
  //   jobList.push({...doc.data(), status, jobRef: doc.ref});
  // }));

  // // const matches = await getMatches(app, jobList?.map((job) => job.jobRef) ?? []);
  // // console.log("in get jobs");
  // // console.log(matches?.map((match) => console.log(match.isMatchedByGigup)));

  // return jobList;
}


async function getMatches(db: Firestore, jobs: DocumentReference<JobDto>[]) {
  if(!jobs.length) return [];

  return (await getDocsIn(
    collection(db, 'guMatches') as CollectionReference<Match>,
    'job', jobs)
  ).map(d => d.data());
}


async function getCandidates(db: Firestore, refs: DocumentReference<Candidate>[]) {
  if(!refs.length) return [];

  return (await getDocsIn(
    collection(db, 'guUsers') as CollectionReference<Candidate>,
    'uid', refs.map(r => r.id))
  ).map(d => d.data());
}


async function getAllCandidates(db: Firestore) {
  return (await getDocs(collection(db, 'guUsers') as CollectionReference<Candidate>)).docs.map(user => user.data());
}


async function getMatchedCandidates(db: Firestore, jobId: string) {
  if (jobId) {
    const jobRef = doc(collection(db, 'guJobs') as CollectionReference<JobDto>, jobId);
    return getCandidates(db, (await getMatches(db, [jobRef])).map(m => m.candidate));
  }
  return [];
}


async function saveMatchedCandidates(db: Firestore, jobId: string, oldCandidates: Candidate[], newCandidates: Candidate[]) {
  const matchesCollection = collection(db, 'guMatches') as CollectionReference<Match>

  const toDelete = oldCandidates.filter(oldC => !newCandidates.find(m => oldC.uid === m.uid));
  const toAdd = newCandidates.filter(newC => !oldCandidates.find(m => newC.uid === m.uid));

  toDelete.forEach(candidate => {
    const q = query(matchesCollection,
      where('candidate', '==', doc(db, 'guUsers', candidate.uid))
    );
    getDocs(q).then(matches => matches.forEach(match => deleteDoc(match.ref)))
  });

  const allMatches = await getAllMatches(db);
  const match = allMatches.find(match => match.name === 'Candidate Interested')!;
  toAdd.forEach(candidate => {
    addDoc(matchesCollection, {
      candidate: doc(collection(db, 'guUsers'), candidate.uid),
      job: doc(collection(db, 'guJobs'), jobId),
      isMatchedByGigup: true,
      matchStatus: doc(collection(db, 'guRefMatchStatuses'), match.uid),
      dateCreated: serverTimestamp()
    });
  });
}


function buildJobDto(db: Firestore, job: UpdateJobDto) {
  const { jobtypes, industries, firstDay, lastDay, weekRanges, ...dto } = job;

  const jobsCollection = collection(db, 'guRefJobs') as CollectionReference<Jobtype>;
  const jobtypesRef = jobtypes.map(jobtype => doc(jobsCollection, jobtype.uid));

  const industriesCollection = collection(db, 'guRefJobs') as CollectionReference<Jobtype>;
  const industriesRef = industries.map(industry => doc(industriesCollection, industry.uid));

  const res = { ...dto,
    jobtypes: jobtypesRef,
    industries: industriesRef,
    firstDay: Timestamp.fromDate(firstDay),
    lastDay: lastDay ? Timestamp.fromDate(lastDay) : undefined,
    dateCreated: serverTimestamp(),
    weekRanges: weekRanges.map(day =>
      ({ dayRanges: day.dayRanges.map(range => ({
        start: range.start && Timestamp.fromDate(range.start),
        end: range.end && Timestamp.fromDate(range.end)
      }))})
    ) as unknown as WeekRanges<Timestamp>
  }
  return res;
}


async function addJob(db: Firestore, job: AddJobDto, employer: Employer) {
  await addDoc(collection(db, 'guJobs') as CollectionReference<AddJobDto & { dateCreated: Timestamp, jid: number, employer: DocumentReference<Employer> }>,
    { ...buildJobDto(db, job), jid: employer.nextJobId, employer: employer.ref }
  );

  employer.nextJobId += 1;
  employer.jobsCount += 1;
  await updateDoc(employer.ref, { nextJobId: employer.nextJobId, jobsCount: employer.jobsCount });
}


async function updateJob(db: Firestore, id: string, job: UpdateJobDto) {
  const ref = doc(collection(db, 'guJobs') as CollectionReference<AddJobDto & { dateCreated: Timestamp }>, id);
  await setDoc(ref, buildJobDto(db, job), { merge: true });
}


async function deleteJob(job: Job) {
  await deleteDoc(job.ref); // delete job doc
  const emp = await (await getDoc(job.employer)).data() as Employer;
  await updateDoc(job.employer, { jobsCount: emp.jobsCount - 1 });
}


export function JobsProvider({ children }: { children: React.ReactNode }) {
  const { firebaseApp, db } = useFirebase();
  const { allJobtypes, allIndustries } = useGigup();

  const context = useMemo<JobsContextType>(() => ({
    getJobs: () => getJobs(db, allIndustries, allJobtypes),
    getEmployerJobs: (id) => getEmployerJobs(db, id, allIndustries, allJobtypes),
    getJobById: async (id) => getJobById(db, id, allIndustries, allJobtypes),
    getAllCandidates: async () => getAllCandidates(db),
    getMatchedCandidates: async (jobId) => getMatchedCandidates(db, jobId),
    saveMatchedCandidates: async (jobId, oldCandidates, newCandidates) => saveMatchedCandidates(db, jobId, oldCandidates, newCandidates),
    addJob: async (job, employer) => addJob(db, job, employer),
    updateJob: async (id, job) => updateJob(db, id, job),
    deleteJob: async (job) => deleteJob(job),
  }), [firebaseApp, db, allJobtypes, allIndustries]);

  return <JobsContext.Provider value={context}>{children}</JobsContext.Provider>;
}


export function useJobs() {
  return useContext(JobsContext);
}
